import * as React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withRouter } from 'react-router-dom';

import { TimelineLite, Expo } from 'gsap';
import { brandColors, device, fonts } from '../../styles/variables';
import { actionOverlay } from '../../store/Overlay';

const Subtitle = styled.p`
  ${({ lineBreak }) => `
    color: #ffffff;
    font-family: ${fonts.athelas};
    font-size: 18px;
    font-style: italic;
    font-weight: normal;
    letter-spacing: 1px;
    display: ${lineBreak ? 'block' : 'inline-block'};
    line-height: 39px;
    vertical-align: middle;
    margin: 20px 0 0 0;
    @media ${device.tablet} {
      margin: 20px 23px 20px 0;
    }
  `};
`;

const LinkText = styled.h1`
  ${({ color, active, listV, tinyV, lineTextWidth, comingSoon }) => `
    width: 100%;
    max-width: ${
      lineTextWidth !== '' ? lineTextWidth : tinyV ? '153px' : listV ? '276px' : '200px'
    };
    color: ${color};
    font-family: ${fonts.monterrat};
    font-weight: bold;
    cursor: ${active ? 'pointer' : 'default'};
    margin: ${listV ? '0' : '-6px 0 0 0'};
    font-size: 20px;
    line-height: 26px;
    opacity: ${comingSoon ? 0.6 : 1};
    @media ${device.tablet} {
      font-size: ${tinyV ? '20px' : '36px'};
      line-height: ${tinyV ? '26px' : '42px'};
    }
  `};
`;

const Line = styled.div`
  ${({ listV, lineBreak, lineColor }) => `
    height: 1px;
    background-color: ${lineColor};
    width: 48px;
    margin: 11px 0px 20px 0px;
    @media ${device.tablet} {
      display: ${lineBreak ? 'block' : 'inline-block'};
      vertical-align: middle;
      margin: ${listV ? '21px 0' : '20px 0px'};
    }
  `};
`;

const ComingSoonText = styled.p`
  color: ${brandColors.white};
  font-style: italic;
`;

class LineLink extends React.PureComponent {
  constructor(props) {
    super(props);
    this.line = React.createRef();
    this.timeline = new TimelineLite({ paused: true });
  }

  componentDidMount() {
    const { lineMaxWidth, triggerAnimation, reverseAnimation } = this.props;
    this.timeline.to(this.line.current, 0.8, {
      width: lineMaxWidth,
      ease: Expo.easeInOut
    });

    if (triggerAnimation) {
      this.animation(reverseAnimation);
    }
  }

  componentDidUpdate(prevProps) {
    const { triggerAnimation, reverseAnimation } = this.props;

    if (prevProps.triggerAnimation !== triggerAnimation) {
      this.animation(reverseAnimation);
    }
  }

  animation(reverse = false) {
    const { lineAnimate } = this.props;

    if (!lineAnimate) {
      return false;
    }

    return reverse ? this.timeline.reverse() : this.timeline.play();
  }

  linkAction() {
    const { history, route, overlay, triggerOverlay, linkOverlayType, comingSoon } = this.props;
    if (comingSoon) {
      return false;
    }

    if (route) {
      history.push(route);

      // need to close the overlay if it is open
      if (overlay.opened) {
        triggerOverlay({
          opened: false,
          overlayType: overlay.overlayType
        });
      }
    } else if (linkOverlayType !== '') {
      triggerOverlay({
        opened: true,
        overlayType: linkOverlayType
      });
    }

    return true;
  }

  render() {
    const {
      className,
      subtitle,
      linkText,
      linkColor,
      active,
      lineWidth,
      listV,
      lineBreak,
      tinyV,
      lineTextWidth,
      triggerAnimation,
      lineColor,
      comingSoon
    } = this.props;

    let showSubtitle;

    if (subtitle) {
      showSubtitle = <Subtitle lineBreak={lineBreak}>{subtitle}</Subtitle>;
    }

    let showComingSoon;

    if (comingSoon) {
      showComingSoon = <ComingSoonText>coming soon</ComingSoonText>;
    }

    return (
      <div
        className={className}
        onMouseEnter={() => (!triggerAnimation ? this.animation() : null)}
        onMouseLeave={() => (!triggerAnimation ? this.animation(true) : null)}
      >
        {showSubtitle}
        <Line
          innerRef={this.line}
          width={lineWidth}
          listV={listV}
          lineBreak={lineBreak}
          lineColor={lineColor}
        />
        <LinkText
          color={linkColor}
          active={active}
          onClick={() => this.linkAction()}
          listV={listV}
          tinyV={tinyV}
          lineTextWidth={lineTextWidth}
          comingSoon={comingSoon}
        >
          {linkText}
        </LinkText>
        {showComingSoon}
      </div>
    );
  }
}

LineLink.defaultProps = {
  className: '',
  subtitle: null,
  linkColor: brandColors.white,
  route: null,
  active: false,
  lineWidth: '48px',
  lineAnimate: false,
  lineMaxWidth: '50%',
  listV: false,
  lineBreak: false,
  tinyV: false,
  lineTextWidth: '',
  triggerAnimation: false,
  reverseAnimation: false,
  lineColor: brandColors.white,
  overlay: {},
  linkOverlayType: '',
  comingSoon: false
};

LineLink.propTypes = {
  className: PropTypes.string,
  history: PropTypes.any.isRequired,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  linkText: PropTypes.string.isRequired,
  linkColor: PropTypes.string,
  route: PropTypes.string,
  active: PropTypes.bool,
  lineWidth: PropTypes.string,
  lineAnimate: PropTypes.bool,
  lineMaxWidth: PropTypes.string,
  listV: PropTypes.bool,
  lineBreak: PropTypes.bool,
  tinyV: PropTypes.bool,
  lineTextWidth: PropTypes.string,
  triggerAnimation: PropTypes.bool,
  reverseAnimation: PropTypes.bool,
  lineColor: PropTypes.string,
  overlay: PropTypes.object,
  triggerOverlay: PropTypes.func.isRequired,
  linkOverlayType: PropTypes.string,
  comingSoon: PropTypes.bool
};

const mapStateToProps = state => ({
  overlay: state.overlay
});

const mapDispatchToProps = dispatch => ({
  triggerOverlay(overlayState) {
    dispatch(actionOverlay(overlayState));
  }
});

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(LineLink)
);



// WEBPACK FOOTER //
// ./src/components/LineLink/index.jsx